import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { theme, btnPrimary, type as text } from "../theme";
import { api, ApiError, apiErrorMessage } from "../api/client";
import { ModalOverlay, ModalHeader, Field, input, textarea, secondaryBtn } from "../pages/resilience/shared";
import { todayInput } from "../lib/dateOnly";
import type { StepDetail } from "../lib/recommendationTasks";

// Turns one recommended action into a corrective action whose steps are each given to a member
// (POST /improvement/actions/from-recommendation, lib/recommendationTasks.ts on the API). People the
// AI named are pre-filled only where the API matched them to a member exactly; anyone else is shown
// as named and left for the person raising the tasks to assign or leave unassigned.

interface Member { id: string; name: string }

interface StepForm { title: string; ownerUserId: string; named: string[]; unmatched: string[]; dueDate: string }

const PRIORITIES = ["critical", "high", "medium", "low"] as const;

export function RecommendationTasksDialog({ recommendation, steps, frameworkId, controlId, onClose, onCreated }: {
  recommendation: string;
  steps: StepDetail[];
  frameworkId: string;
  controlId: string;
  onClose: () => void;
  onCreated: (action: { id: string; displayId: number | null }, stepCount: number) => void;
}) {
  const [members, setMembers] = useState<Member[] | null>(null);
  const [rows, setRows] = useState<StepForm[]>(() => steps.map((s) => {
    const matched = s.people.filter((p) => p.userId);
    return {
      title: s.text,
      ownerUserId: matched[0]?.userId ?? "",
      named: s.people.map((p) => p.name),
      unmatched: s.people.filter((p) => !p.userId).map((p) => p.name),
      dueDate: "",
    };
  }));
  const [ownerUserId, setOwnerUserId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState<(typeof PRIORITIES)[number]>("medium");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [already, setAlready] = useState<{ id: string; displayId: number | null } | null>(null);

  useEffect(() => {
    api.get<{ members: Array<{ id: string; displayName: string | null; email: string; isActive?: boolean }> }>("/organizations/members")
      .then((d) => setMembers(d.members.filter((m) => m.isActive !== false).map((m) => ({ id: m.id, name: m.displayName || m.email }))))
      .catch(() => setMembers([]));
  }, []);

  const firstAssignee = rows.find((r) => r.ownerUserId)?.ownerUserId ?? "";
  const memberName = (id: string) => members?.find((m) => m.id === id)?.name ?? "";
  const update = (i: number, patch: Partial<StepForm>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const today = todayInput();

  async function submit() {
    const kept = rows.filter((r) => r.title.trim());
    if (!kept.length) { setError("Keep at least one step."); return; }
    setBusy(true); setError(null);
    try {
      const res = await api.post<{ action: { id: string; displayId: number | null } }>("/improvement/actions/from-recommendation", {
        frameworkId, controlId, recommendation, priority,
        ownerUserId: ownerUserId || null,
        dueDate: dueDate || null,
        steps: kept.map((r) => ({
          title: r.title.trim(),
          ownerUserId: r.ownerUserId || null,
          // A name that matched no member is kept as written, so the step still says who it is for.
          ownerName: r.ownerUserId ? null : (r.unmatched[0] ?? null),
          dueDate: r.dueDate || null,
        })),
      });
      onCreated(res.action, kept.length);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && typeof e.payload === "object" && e.payload && "action" in e.payload) {
        setAlready((e.payload as { action: { id: string; displayId: number | null } }).action);
      }
      setError(apiErrorMessage(e, "Could not create the tasks."));
      setBusy(false);
    }
  }

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title="Create tasks from this recommendation" onClose={onClose} />
      <div style={{ padding: 20, display: "grid", gap: 16 }}>
        <div style={{ ...text.body, borderLeft: `2px solid ${theme.color.border}`, paddingLeft: 10, color: theme.color.subtle }}>{recommendation}</div>
        <p style={{ ...text.hint, margin: 0, maxWidth: 760 }}>
          This raises one corrective action with a step for each line below. Each step goes on the My tasks list of the person you give it to, and they are emailed. People the AI named are filled in only where the name matches a member exactly.
        </p>

        <div style={{ display: "grid", gap: 12 }}>
          {rows.map((r, i) => (
            <div key={i} style={{ border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: 12, display: "grid", gap: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <span style={{ ...text.label, marginBottom: 0 }}>Step {i + 1}</span>
                {rows.length > 1 && (
                  <button type="button" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.dangerText, fontSize: text.hint.fontSize, padding: 0 }}>Remove</button>
                )}
              </div>
              <textarea rows={2} value={r.title} maxLength={1000} onChange={(e) => update(i, { title: e.target.value })} style={textarea} aria-label={`Step ${i + 1}`} />
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)", gap: 10 }}>
                <Field label="Given to">
                  <select value={r.ownerUserId} onChange={(e) => update(i, { ownerUserId: e.target.value })} style={input} disabled={!members}>
                    <option value="">Unassigned{r.unmatched[0] ? ` (named: ${r.unmatched[0]})` : ""}</option>
                    {(members ?? []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </Field>
                <Field label="Due">
                  <input type="date" min={today} value={r.dueDate} onChange={(e) => update(i, { dueDate: e.target.value })} style={input} />
                </Field>
              </div>
              {r.unmatched.length > 0 && !r.ownerUserId && (
                <div style={{ ...text.hint, color: theme.color.warningText }}>
                  {r.unmatched.join(", ")} {r.unmatched.length === 1 ? "is" : "are"} not a member of this organisation. Give the step to a member, or leave it unassigned and the name stays on the step.
                </div>
              )}
              {r.named.length > 1 && (
                <div style={text.hint}>This step names {r.named.join(" and ")}. It goes to one person{r.ownerUserId ? ` (${memberName(r.ownerUserId)})` : ""}; the others are in its wording.</div>
              )}
            </div>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
          <Field label="Action owner" help={!ownerUserId && firstAssignee ? `Defaults to ${memberName(firstAssignee)}, the first step's person.` : "Answers for the action as a whole."}>
            <select value={ownerUserId} onChange={(e) => setOwnerUserId(e.target.value)} style={input} disabled={!members}>
              <option value="">{firstAssignee ? "First step's person" : "No owner yet"}</option>
              {(members ?? []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </Field>
          <Field label="Action due" help="Defaults to the latest step's date.">
            <input type="date" min={today} value={dueDate} onChange={(e) => setDueDate(e.target.value)} style={input} />
          </Field>
          <Field label="Priority">
            <select value={priority} onChange={(e) => setPriority(e.target.value as (typeof PRIORITIES)[number])} style={input}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
            </select>
          </Field>
        </div>

        {error && (
          <div style={{ ...text.body, color: theme.color.dangerText }}>
            {error}
            {already && <> <Link to={`/improvement?action=${already.id}`}>Open CA-{already.displayId}</Link></>}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
          <button type="button" disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }} onClick={() => void submit()}>
            {busy ? "Creating…" : `Create ${rows.length === 1 ? "1 task" : `${rows.length} tasks`}`}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
